import React, { useEffect, useState } from 'react'
import type { TableColumnsType } from 'antd'
import { Button, Input, Space, Table } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import { useFiltersV2 } from '../../contexts/FiltersContextV2'
import useSuppliersView from '../../hooks/useSuppliersView'
import { Depot, Supplier } from '../../types'
import { StyledButton, StyledRow } from '../StyledComponents'
import { MapsModal } from '../MapsModal/MapsModal'

const SupplierView: React.FC = () => {
  const { filters, handleResetFilters, handleFilterChange, resetFilters } = useFiltersV2()
  const { suppliers, isLoading } = useSuppliersView({ filters })
  const { t } = useTranslation()
  const [expandedRowKey, setExpandedRowKey] = useState<string | number | null>(null)

  const [isMapsModalOpen, setIsMapsModalOpen] = useState(false)
  const [locationLatitude, setLocationLatitude] = useState<number | undefined>(undefined)
  const [locationLongitude, setLocationLongitude] = useState<number | undefined>(undefined)
  const [locationStreet, setLocationStreet] = useState<string[] | string | undefined>(undefined)
  const [locationCity, setLocationCity] = useState<string[] | string | undefined>(undefined)
  const [locationState, setLocationState] = useState<string[] | string | undefined>(undefined)
  const [locationNumber, setLocationNumber] = useState<string[] | string | undefined>(undefined)
  const [tag, setTag] = useState<string | undefined>(undefined)
  const [partialNameValue, setPartialNameValue] = useState<string>()

  const handleOpenMapsModal = (depot: Depot) => {
    setIsMapsModalOpen(true)
    if (depot.location?.coordinates) {
      setLocationLatitude(depot.location.coordinates[0])
      setLocationLongitude(depot.location.coordinates[1])
      setLocationStreet(depot.street || '')
      setLocationNumber(depot.number || '')
      setLocationState(depot.state || '')
      setLocationCity(depot.city || '')
      setTag(depot.tag || '')
    }
  }

  const expandedRowRender = (record: Supplier) => {
    const columnsDepotDate: TableColumnsType<Depot> = [
      { title: t('data_address.depot'), dataIndex: 'tag', key: 'tag' },
      { title: t('data_address.street'), dataIndex: 'street', key: 'street' },
      { title: t('data_address.number'), dataIndex: 'number', key: 'number' },
      { title: t('data_address.city'), dataIndex: 'city', key: 'city' },
      { title: t('data_address.code'), dataIndex: ['sapGeo', 'cityCode'], key: 'sapGeo.cityCode' },
      { title: t('data_address.state'), dataIndex: 'state', key: 'state' },
      {
        title: 'Acciones',
        key: 'action',
        render: (_: any, depot: Depot) => (
          <Space
            size="middle"
            style={{
              display: 'flex',
              marginLeft: '20px',
              height: '100%',
            }}
          >
            <Button type="link" icon={<EyeOutlined />} onClick={() => handleOpenMapsModal(depot)} />
          </Space>
        ),
      },
    ]

    return <Table columns={columnsDepotDate} dataSource={record.depots} pagination={false} />
  }

  const columnsSuppliers: TableColumnsType<Supplier> = [
    { title: t('data_address.supplier'), dataIndex: 'tradeName', key: 'supplier' },
    { title: t('data_address.email'), dataIndex: 'email', key: 'email' },
    { title: t('data_address.cellphone'), dataIndex: 'cellphone', key: 'cellphone' },
    { title: t('data_address.city'), dataIndex: 'city', key: 'city' },
    { title: t('data_address.sap_code'), dataIndex: 'sapId', key: 'sapId' },
  ]

  const handleSearchText = (value?: string | null | undefined) => {
    if (value === '' || !value) {
      handleFilterChange('partialName')(undefined)
    } else {
      handleFilterChange('partialName')(value)
    }
  }

  const setupRowClassName = (_record: any, index: number) =>
    `expandable-row ${index % 2 === 0 ? 'odd-row' : 'even-row'}`

  const handleExpand = (expanded: boolean, record: Supplier) => {
    setExpandedRowKey(expanded ? record.id : null)
  }

  const handleCleanFilters = () => {
    handleResetFilters()
  }

  const handleCloseMapsModal = () => {
    setIsMapsModalOpen(false)
  }

  const resetViewFilters = () => {
    setPartialNameValue(undefined)
  }

  useEffect(() => {
    if (!!resetFilters) {
      resetViewFilters()
    }
  }, [resetFilters])

  return (
    <>
      <StyledRow className="sticky-options border-botom">
        <div
          className="filters-container"
          style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
        >
          <div
            className="filters-list"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1rem',
              marginLeft: '1rem',
            }}
          >
            <StyledButton className="inline option" onClick={handleCleanFilters}>
              {t('globals.generics.clean_filter')}
            </StyledButton>
            <Input
              style={{ width: '25%', minWidth: '20rem' }}
              placeholder={t('tpl_dashboard.search_name')}
              type="string"
              value={partialNameValue}
              onPressEnter={e => {
                handleSearchText(e.currentTarget.value)
              }}
              onKeyUp={e => {
                setPartialNameValue(e.currentTarget.value)
              }}
              onBlur={e => {
                handleSearchText(e.currentTarget.value)
              }}
              onChange={e => {
                setPartialNameValue(e.currentTarget.value)
                if (e.currentTarget.value === '' || !e.currentTarget.value) {
                  handleFilterChange('partialName')(undefined)
                }
              }}
              allowClear
            />
          </div>
          <Table
            columns={columnsSuppliers}
            loading={isLoading}
            expandable={{
              expandedRowRender,
              expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
              onExpand: handleExpand,
            }}
            dataSource={suppliers?.data}
            rowClassName={setupRowClassName}
            rowKey="id"
            style={{ width: '100%' }}
            pagination={{
              pageSize: filters.limit as number,
              onChange: handleFilterChange('page', false),
              total: suppliers?.total,
              showSizeChanger: false,
              current: filters.page as number,
            }}
            onRow={record => ({
              onClick: () => {
                const isExpanded = expandedRowKey === record.id
                setExpandedRowKey(isExpanded ? null : record.id)
              },
            })}
          />
        </div>
      </StyledRow>
      <MapsModal
        open={isMapsModalOpen}
        longitude={locationLongitude}
        latitude={locationLatitude}
        street={locationStreet}
        number={locationNumber}
        province={locationState}
        city={locationCity}
        close={handleCloseMapsModal}
        tag={tag}
      />
    </>
  )
}

export default SupplierView
